import { createBrowserHistory, History, Location } from 'history';
import { matchPath } from 'react-router-dom';

/**
 * Storage adapter interface for persisting query parameters
 */
export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Default localStorage adapter
 */
export const localStorageAdapter: StorageAdapter = {
  getItem: (key: string) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      console.error('Error storing persisted navigation params:', error);
    }
  },
  removeItem: (key: string) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing persisted navigation params:', error);
    }
  }
};

export interface PatternConfig<QueryParams extends object> {
  /**
   * Pattern to match routes (React Router path syntax with params like :workspaceId)
   */
  pattern: string;

  /**
   * Full localStorage key for this match, or null if required route params are missing.
   */
  getPersistenceKey: (routeParams: Record<string, string>) => string | null;

  /**
   * Parse query params from URL search string
   */
  parseQueryParams: (search: string) => QueryParams;

  /**
   * Serialize query params to URL search string (without the "?")
   */
  serializeQueryParams: (params: QueryParams) => string;
}

export interface PersistentNavigationConfig<QueryParams extends object> {
  /**
   * Patterns to match routes that should have persistent navigation
   */
  patterns: Array<PatternConfig<QueryParams>>;
  /**
   * Optional history instance to use. If not provided, a new browser history will be created.
   * Useful for testing with mocked history instances.
   */
  history?: History;
  /**
   * Storage adapter to use for persisting query parameters.
   * Defaults to localStorageAdapter if not provided.
   */
  storage?: StorageAdapter;
}

type MatchedPattern<QueryParams extends object> = {
  pattern: PatternConfig<QueryParams>;
  routeParams: Record<string, string>;
  persistenceKey: string;
};

/**
 * Creates a browser history instance with persistent query parameters support
 *
 * This function creates a history instance and patches it to automatically:
 * - Store query parameters when navigating with them
 * - Restore query parameters when navigating without them (if previously stored)
 */
export function createPersistentBrowserHistory<QueryParams extends object>(
  config: PersistentNavigationConfig<QueryParams>
): History {
  const history = config.history ?? createBrowserHistory();
  const storage = config.storage ?? localStorageAdapter;
  const originalPush = history.push.bind(history);
  const originalReplace = history.replace.bind(history);

  const extractLocation = (
    to: string | { pathname?: string; search?: string }
  ): { pathname: string; search: string } => {
    if (typeof to === 'string') {
      const [pathname = '', search = ''] = to.split('?');
      return { pathname, search: search ? `?${search}` : '' };
    }
    return {
      pathname: to.pathname || '',
      search: to.search || ''
    };
  };

  const matchRouteParams = (
    path: string,
    pattern: string
  ): Record<string, string> | null => {
    let match = matchPath<Record<string, string>>(path, {
      path: pattern,
      exact: true,
      strict: false
    });

    if (!match && pattern.endsWith('/*')) {
      const patternWithoutWildcard = pattern.slice(0, -2);
      match = matchPath<Record<string, string>>(path, {
        path: patternWithoutWildcard,
        exact: true,
        strict: false
      });
    }

    if (!match?.params) {
      return null;
    }

    const routeParams: Record<string, string> = {};
    for (const [key, value] of Object.entries(match.params)) {
      if (value != null) {
        routeParams[key] = value;
      }
    }
    return routeParams;
  };

  const getMatchedPattern = (
    path: string
  ): MatchedPattern<QueryParams> | null => {
    for (const patternConfig of config.patterns) {
      const routeParams = matchRouteParams(path, patternConfig.pattern);
      if (!routeParams) {
        continue;
      }
      const persistenceKey = patternConfig.getPersistenceKey(routeParams);
      if (!persistenceKey) {
        continue;
      }
      return {
        pattern: patternConfig,
        routeParams,
        persistenceKey
      };
    }
    return null;
  };

  const hasQueryParams = (search: string): boolean => {
    return Boolean(search && search.length > 1);
  };

  const readStoredParams = (persistenceKey: string): QueryParams | null => {
    const stored = storage.getItem(persistenceKey);
    if (!stored) {
      return null;
    }
    try {
      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }
      if (Object.keys(parsed).length === 0) {
        return null;
      }
      return parsed as QueryParams;
    } catch {
      return null;
    }
  };

  const shouldRestoreSync = (
    pathname: string,
    search: string
  ): { pathname: string; search: string } | null => {
    if (hasQueryParams(search)) {
      return null;
    }

    const matched = getMatchedPattern(pathname);
    if (!matched) {
      return null;
    }

    try {
      const persistedData = readStoredParams(matched.persistenceKey);
      if (!persistedData) {
        return null;
      }

      const restoredSearch =
        '?' + matched.pattern.serializeQueryParams(persistedData);
      return {
        pathname,
        search: restoredSearch
      };
    } catch (error) {
      console.error(
        '[PersistentNavigation] shouldRestoreSync: error restoring params:',
        error
      );
      return null;
    }
  };

  history.push = ((to: any, state?: any) => {
    const { pathname, search } = extractLocation(to);

    if (hasQueryParams(search)) {
      return originalPush(to, state);
    }

    const syncResult = shouldRestoreSync(pathname, search);
    if (syncResult) {
      return originalPush(
        {
          pathname: syncResult.pathname,
          search: syncResult.search
        },
        state
      );
    }

    return originalPush(to, state);
  }) as typeof history.push;

  history.replace = ((to: any, state?: any) => {
    const { pathname, search } = extractLocation(to);

    if (hasQueryParams(search)) {
      return originalReplace(to, state);
    }

    const syncResult = shouldRestoreSync(pathname, search);
    if (syncResult) {
      return originalReplace(
        {
          pathname: syncResult.pathname,
          search: syncResult.search
        },
        state
      );
    }

    return originalReplace(to, state);
  }) as typeof history.replace;

  const storeCurrentLocationParams = (location: Location) => {
    const { pathname, search } = location;

    if (!hasQueryParams(search)) {
      return;
    }

    try {
      const matched = getMatchedPattern(pathname);
      if (!matched) {
        return;
      }
      const params = matched.pattern.parseQueryParams(search);
      storage.setItem(matched.persistenceKey, JSON.stringify(params));
    } catch (error) {
      console.error(
        '[PersistentNavigation] storeCurrentLocationParams: error storing params:',
        error
      );
    }
  };

  const currentLocation = history.location;
  const { pathname, search } = currentLocation;

  if (hasQueryParams(search)) {
    storeCurrentLocationParams(currentLocation);
  } else {
    const syncResult = shouldRestoreSync(pathname, search);
    if (syncResult) {
      const newLocation = `${syncResult.pathname}${syncResult.search}`;
      const currentLocationStr = `${pathname}${search}`;
      if (newLocation !== currentLocationStr) {
        originalReplace({
          pathname: syncResult.pathname,
          search: syncResult.search
        });
      }
    }
  }

  const unlisten = history.listen(location => {
    storeCurrentLocationParams(location);
  });

  (history as any)._unlistenPersistentNavigation = unlisten;

  return history;
}
